import { useCallback, useEffect, useState } from 'react';
import { COUNTRY_COOKIE_NAME } from '../lib/cookies/country-cookie';

const SEEN_KEY = 'uk_waitlist_modal_seen';
const SEEN_TTL_MS = 30 * 24 * 60 * 60 * 1000;

const UK_COUNTRIES = ['GB', 'JE', 'GG', 'IM'];

const readCookie = (name: string): string | null =>
  document.cookie
    .split('; ')
    .find(c => c.startsWith(`${name}=`))
    ?.split('=')[1] ?? null;

const hasSeen = (): boolean => {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    if (!raw) return false;
    const { expiresAt } = JSON.parse(raw);
    if (typeof expiresAt !== 'number' || Date.now() > expiresAt) {
      localStorage.removeItem(SEEN_KEY);
      return false;
    }
    return true;
  } catch {
    localStorage.removeItem(SEEN_KEY);
    return false;
  }
};

const markSeen = (): void => {
  try {
    localStorage.setItem(
      SEEN_KEY,
      JSON.stringify({ expiresAt: Date.now() + SEEN_TTL_MS }),
    );
  } catch {
    // Safari private mode / storage disabled — modal just reappears next visit
  }
};

export const useUkWaitlistModal = () => {
  const [isOpen, setIsOpen] = useState(false);

  // Client-only: pages are CDN-cached, this must never render on the server
  useEffect(() => {
    if (UK_COUNTRIES.includes(readCookie(COUNTRY_COOKIE_NAME)) && !hasSeen()) {
      setIsOpen(true);
    }
  }, []);

  const dismiss = useCallback(() => {
    markSeen();
    setIsOpen(false);
  }, []);

  return { isOpen, dismiss, markSeen };
};
